import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { GATE_DEFAULTS, pointAlong, bounds } from "../../lib/planner";

const GRID = 100;
const snapGrid = (v) => Math.round(v / GRID) * GRID;

export function PlannerCanvas({ plan, layout, mode, ortho, onAddPoint, onMovePoint, onClose, selectedSeg, onSelectSeg, concrete, ghosts, fitPoints }) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 520 });
  const [view, setView] = useState({ cx: 5000, cy: 3000, scale: 0.05 });
  const [cursor, setCursor] = useState(null);
  const drag = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth - 2, h: 520 }));
    ro.observe(el);
    setSize({ w: el.clientWidth - 2, h: 520 });
    return () => ro.disconnect();
  }, []);

  const toWorld = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: view.cx + (e.clientX - r.left - r.width / 2) / view.scale, y: view.cy + (e.clientY - r.top - r.height / 2) / view.scale };
  };
  const snap = (p, ref) => {
    let q = { x: snapGrid(p.x), y: snapGrid(p.y) };
    if (ortho && ref) q = Math.abs(q.x - ref.x) >= Math.abs(q.y - ref.y) ? { x: q.x, y: ref.y } : { x: ref.x, y: q.y };
    return q;
  };
  const fit = () => {
    const b = bounds((fitPoints && fitPoints.length ? fitPoints : plan.points));
    const w = Math.max(b.maxX - b.minX, 4000), h = Math.max(b.maxY - b.minY, 3000);
    setView({ cx: (b.minX + b.maxX) / 2, cy: (b.minY + b.maxY) / 2, scale: Math.min(size.w / (w * 1.35), size.h / (h * 1.35)) });
  };
  const zoom = (f) => setView((v) => ({ ...v, scale: Math.min(0.6, Math.max(0.008, v.scale * f)) }));

  const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15); };
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const w = toWorld(e);
    const hitIdx = plan.points.findIndex((p) => Math.hypot(p.x - w.x, p.y - w.y) < 12 / view.scale);
    if (mode === "draw") {
      if (hitIdx === 0 && plan.points.length >= 3) { onClose(); return; }
      const last = plan.points[plan.points.length - 1];
      onAddPoint(snap(w, last));
      return;
    }
    if (hitIdx >= 0) { drag.current = { type: "point", idx: hitIdx }; return; }
    drag.current = { type: "pan", start: { x: e.clientX, y: e.clientY }, view: { ...view } };
  };
  const onPointerMove = (e) => {
    const w = toWorld(e);
    if (drag.current?.type === "point") {
      const i = drag.current.idx;
      const ref = plan.points[i - 1] || plan.points[i + 1];
      onMovePoint(i, snap(w, ref));
      return;
    }
    if (drag.current?.type === "pan") {
      const d = drag.current;
      setView({ ...d.view, cx: d.view.cx - (e.clientX - d.start.x) / d.view.scale, cy: d.view.cy - (e.clientY - d.start.y) / d.view.scale });
      return;
    }
    const last = plan.points[plan.points.length - 1];
    setCursor(mode === "draw" ? snap(w, last) : null);
  };
  const onPointerUp = () => { drag.current = null; };

  const vb = `${view.cx - size.w / 2 / view.scale} ${view.cy - size.h / 2 / view.scale} ${size.w / view.scale} ${size.h / view.scale}`;
  const px = (n) => n / view.scale; // n px expressed in mm
  const last = plan.points[plan.points.length - 1];
  const previewLen = cursor && last && !plan.closed ? Math.round(Math.hypot(cursor.x - last.x, cursor.y - last.y)) : 0;

  return (
    <div ref={wrapRef} className="relative border border-zinc-300 bg-white select-none" data-testid="planner-canvas-wrap">
      <svg ref={svgRef} width={size.w} height={size.h} viewBox={vb} data-testid="planner-canvas"
           className={mode === "draw" ? "cursor-crosshair" : "cursor-move"}
           onWheel={onWheel} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}>
        <defs>
          <pattern id="grid-s" width={500} height={500} patternUnits="userSpaceOnUse">
            <path d="M 500 0 L 0 0 0 500" fill="none" stroke="#e4e4e7" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          </pattern>
          <pattern id="grid-l" width={2500} height={2500} patternUnits="userSpaceOnUse">
            <rect width={2500} height={2500} fill="url(#grid-s)" />
            <path d="M 2500 0 L 0 0 0 2500" fill="none" stroke="#c9c9cf" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          </pattern>
        </defs>
        <rect x={view.cx - 1e6} y={view.cy - 1e6} width={2e6} height={2e6} fill="url(#grid-l)" />
        {(ghosts || []).map((g, gi) => (
          <g key={`ghost-${gi}`} pointerEvents="none" data-testid={`planner-ghost-${gi}`} opacity={0.5}>
            {g.layout.segments.map((s) => {
              const mid = pointAlong(s, s.length / 2);
              const nx = -s.dir.y, ny = s.dir.x;
              return (
                <g key={s.idx}>
                  <line x1={s.a.x} y1={s.a.y} x2={s.b.x} y2={s.b.y} stroke={g.color} strokeWidth={4} vectorEffect="non-scaling-stroke" opacity={0.7} />
                  {s.elements.filter((e) => e.kind === "field").map((el, i) => {
                    const p1 = pointAlong(s, el.from), p2 = pointAlong(s, el.to);
                    return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={g.color} strokeWidth={2} vectorEffect="non-scaling-stroke" />;
                  })}
                  <text x={mid.x + nx * px(22)} y={mid.y + ny * px(22) + px(4)} fontSize={px(11)} textAnchor="middle" fill={g.color} fontWeight="700">{g.name}</text>
                </g>
              );
            })}
          </g>
        ))}
        {concrete && (
          <polygon points={[...concrete.outer, ...[...concrete.inner].reverse()].map((p) => `${p.x},${p.y}`).join(" ")}
                   fill="#c7c7cc" fillOpacity={0.55} stroke="#71717a" strokeWidth={1} vectorEffect="non-scaling-stroke" data-testid="planner-concrete" />
        )}

        {layout.segments.map((s) => {
          const mid = pointAlong(s, s.length / 2);
          const nx = -s.dir.y, ny = s.dir.x;
          const anchor = Math.abs(nx) > 0.5 ? (nx > 0 ? "start" : "end") : "middle";
          const sel = selectedSeg === s.idx;
          return (
            <g key={s.idx} onPointerDown={(e) => { if (mode !== "draw") { e.stopPropagation(); onSelectSeg(s.idx); } }} data-testid={`planner-seg-${s.idx}`}>
              <line x1={s.a.x} y1={s.a.y} x2={s.b.x} y2={s.b.y} stroke={sel ? "#f59e0b" : "#a1a1aa"} strokeWidth={sel ? 10 : 6} vectorEffect="non-scaling-stroke" opacity={0.35} />
              {s.elements.map((el, i) => {
                const p1 = pointAlong(s, el.from), p2 = pointAlong(s, el.to);
                if (el.kind === "field") {
                  const c = pointAlong(s, (el.from + el.to) / 2);
                  return (
                    <g key={i}>
                      <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#18181b" strokeWidth={3} vectorEffect="non-scaling-stroke" />
                      {el.width * view.scale > 44 && (
                        <text x={c.x + nx * px(-12)} y={c.y + ny * px(-12) + px(3)} fontSize={px(10)} textAnchor={anchor === "middle" ? "middle" : anchor === "start" ? "end" : "start"} fill="#52525b" fontFamily="monospace">{el.width}</text>
                      )}
                    </g>
                  );
                }
                if (el.kind === "gate") {
                  const c = pointAlong(s, (el.from + el.to) / 2);
                  return (
                    <g key={i}>
                      <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#2563eb" strokeWidth={5} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
                      <text x={c.x + nx * px(-14)} y={c.y + ny * px(-14) + px(3)} fontSize={px(10)} textAnchor={anchor === "middle" ? "middle" : anchor === "start" ? "end" : "start"} fill="#1d4ed8" fontWeight="600" fontFamily="monospace">
                        {`${GATE_DEFAULTS[el.type]?.short || ""} ${el.width}`}
                      </text>
                    </g>
                  );
                }
                const d = Math.max(el.to - el.from, px(7));
                const c = pointAlong(s, (el.from + el.to) / 2);
                const fill = el.profile === "gate" ? "#2563eb" : el.profile === "corner" ? "#0f172a" : "#3f3f46";
                return <rect key={i} x={c.x - d / 2} y={c.y - d / 2} width={d} height={d} fill={fill} stroke="#fff" strokeWidth={1} vectorEffect="non-scaling-stroke" />;
              })}
              <text x={mid.x + nx * px(22)} y={mid.y + ny * px(22) + px(4)} fontSize={px(12)} textAnchor={anchor} fill="#18181b" fontWeight="700">
                {`${s.label} · ${s.length} mm`}
              </text>
            </g>
          );
        })}

        {plan.points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={px(6)} fill={i === 0 && mode === "draw" && plan.points.length >= 3 && !plan.closed ? "#f59e0b" : "#fff"}
                  stroke="#18181b" strokeWidth={2} vectorEffect="non-scaling-stroke" data-testid={`planner-point-${i}`} />
        ))}
        {cursor && last && !plan.closed && mode === "draw" && (
          <g pointerEvents="none">
            <line x1={last.x} y1={last.y} x2={cursor.x} y2={cursor.y} stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
            <text x={cursor.x + px(10)} y={cursor.y - px(10)} fontSize={px(11)} fill="#b45309" fontFamily="monospace">{previewLen} mm</text>
          </g>
        )}
        {cursor && !last && mode === "draw" && <circle cx={cursor.x} cy={cursor.y} r={px(4)} fill="#f59e0b" pointerEvents="none" />}
      </svg>
      <div className="absolute top-2 right-2 flex flex-col gap-1">
        <button onClick={() => zoom(1.25)} className="bg-white border border-zinc-300 p-1.5 hover:bg-zinc-100" data-testid="planner-zoom-in"><ZoomIn size={14}/></button>
        <button onClick={() => zoom(1 / 1.25)} className="bg-white border border-zinc-300 p-1.5 hover:bg-zinc-100" data-testid="planner-zoom-out"><ZoomOut size={14}/></button>
        <button onClick={fit} className="bg-white border border-zinc-300 p-1.5 hover:bg-zinc-100" data-testid="planner-zoom-fit"><Maximize2 size={14}/></button>
      </div>
      <div className="absolute bottom-2 left-2 text-[10px] text-zinc-500 bg-white/80 px-2 py-1 border border-zinc-200">
        Raster 500 mm · Fang 100 mm{plan.points.length === 0 && mode === "draw" ? " · Klicken Sie, um den ersten Punkt zu setzen" : ""}
        {mode === "draw" && plan.points.length >= 3 && !plan.closed ? " · Klick auf den ersten Punkt schließt den Zaun" : ""}
      </div>
    </div>
  );
}
